import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Download, LogOut, Megaphone, Search } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatError = (err) => {
    const detail = err.response?.data?.detail;
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) return detail.map((e) => e.msg).join(" ");
    return "Something went wrong. Try again.";
};

const MEMBER_STATUSES = ["reserved", "paid", "allocated", "delivered"];

const STATUS_STYLES = {
    reserved: "border-white/25 text-zinc-400",
    paid: "border-[#2563EB] bg-[#2563EB] text-white",
    allocated: "border-white bg-white text-black",
    delivered: "border-emerald-500 bg-emerald-600 text-white",
};

const TABLES = {
    members: {
        label: "Reservations",
        columns: ["Name", "Email", "Phone", "Trim", "Color", "Factory Options", "Reserved", "Status"],
        row: (m) => [
            m.name,
            m.email,
            m.phone,
            m.trim,
            m.color,
            (m.options || []).join(", ") || "—",
            new Date(m.created_at).toLocaleDateString(),
        ],
    },
    spot: {
        label: "Spot Deals",
        columns: ["Name", "Email", "Phone", "Address", "Trim", "Color", "Drivetrain", "Moonroof", "Reserved"],
        row: (s) => [
            s.name,
            s.email,
            s.phone,
            s.address,
            s.trim,
            s.color || "—",
            s.drivetrain,
            s.moonroof,
            new Date(s.created_at).toLocaleDateString(),
        ],
    },
};

const toCsv = (rows, columns) =>
    [columns, ...rows]
        .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(","))
        .join("\n");

export default function Admin() {
    const [auth, setAuth] = useState(null);
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [members, setMembers] = useState([]);
    const [spotDeals, setSpotDeals] = useState([]);
    const [tab, setTab] = useState("members");
    const [search, setSearch] = useState("");
    const [blastOpen, setBlastOpen] = useState(false);
    const [blastForm, setBlastForm] = useState({ vehicle: "", price: "", message: "" });
    const [blastSending, setBlastSending] = useState(false);
    const [alert, setAlert] = useState({ trim: "", price: "", region: "", enabled: true });
    const [alertSaving, setAlertSaving] = useState(false);

    useEffect(() => {
        if (auth) {
            axios
                .get(`${API}/deal-alert`, { withCredentials: true })
                .then((r) => setAlert(r.data))
                .catch(() => {});
        }
    }, [auth]);

    const saveAlert = async (e) => {
        e.preventDefault();
        setAlertSaving(true);
        try {
            const { data } = await axios.put(`${API}/deal-alert`, alert, { withCredentials: true });
            setAlert(data);
            toast.success("Deal alert updated — live on the homepage");
        } catch (err) {
            toast.error(formatError(err));
        } finally {
            setAlertSaving(false);
        }
    };

    const setStatus = async (m, status) => {
        try {
            const { data } = await axios.patch(
                `${API}/members/${m.id}/status`,
                { status },
                { withCredentials: true }
            );
            setMembers((prev) => prev.map((x) => (x.id === m.id ? data : x)));
            toast.success(`${data.name} → ${status}`);
        } catch {
            toast.error("Could not update status");
        }
    };

    const sendBlast = async (e) => {
        e.preventDefault();
        setBlastSending(true);
        try {
            const { data } = await axios.post(`${API}/spot-deals/blast`, blastForm, {
                withCredentials: true,
            });
            const parts = [`${data.emails_sent} emails`, `${data.sms_sent} texts`];
            const off = [];
            if (!data.email_enabled) off.push("email key missing");
            if (!data.sms_enabled) off.push("SMS not configured");
            toast.success(
                `Blast sent to ${data.total_signups} signups — ${parts.join(", ")}${off.length ? ` (${off.join("; ")})` : ""}`
            );
            setBlastOpen(false);
            setBlastForm({ vehicle: "", price: "", message: "" });
        } catch (err) {
            toast.error(formatError(err));
        } finally {
            setBlastSending(false);
        }
    };

    useEffect(() => {
        axios
            .get(`${API}/auth/me`, { withCredentials: true })
            .then((r) => setAuth(r.data))
            .catch(() => setAuth(false));
    }, []);

    useEffect(() => {
        if (auth) {
            axios
                .get(`${API}/members`, { withCredentials: true })
                .then((r) => setMembers(r.data))
                .catch(() => setMembers([]));
            axios
                .get(`${API}/spot-deals`, { withCredentials: true })
                .then((r) => setSpotDeals(r.data))
                .catch(() => setSpotDeals([]));
        }
    }, [auth]);

    const login = async (e) => {
        e.preventDefault();
        setError("");
        try {
            const { data } = await axios.post(
                `${API}/auth/login`,
                { email, password },
                { withCredentials: true }
            );
            setAuth(data);
        } catch (err) {
            setError(formatError(err));
        }
    };

    const logout = async () => {
        await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
        setAuth(false);
    };

    const list = tab === "members" ? members : spotDeals;
    const filtered = useMemo(() => {
        const q = search.toLowerCase();
        return list.filter((m) => Object.values(m).join(" ").toLowerCase().includes(q));
    }, [list, search]);

    const exportCsv = () => {
        const table = TABLES[tab];
        const blob = new Blob([toCsv(filtered.map(table.row), table.columns)], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `rav4-buyers-club-${tab === "members" ? "members" : "spot-deals"}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    if (auth === null) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-[#050505]">
                <p className="text-sm uppercase tracking-[0.3em] text-zinc-500">Loading...</p>
            </div>
        );
    }

    if (auth === false) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-[#050505] px-6">
                <form
                    onSubmit={login}
                    data-testid="admin-login-form"
                    className="w-full max-w-sm space-y-6 border border-white/10 bg-[#0a0a0a] p-8"
                >
                    <div className="flex flex-col items-center gap-4">
                        <img src="/images/logo.png" alt="RAV4 Buyers Club" className="w-24 bg-white p-2" />
                        <h1 className="font-display text-lg font-bold uppercase tracking-[0.2em] text-white">
                            Member Dashboard
                        </h1>
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="admin-email" className="text-zinc-300">Email</Label>
                        <Input
                            id="admin-email"
                            data-testid="admin-email-input"
                            type="email"
                            required
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="h-12 rounded-none border-white/15 bg-black/40 text-white"
                        />
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="admin-password" className="text-zinc-300">Password</Label>
                        <Input
                            id="admin-password"
                            data-testid="admin-password-input"
                            type="password"
                            required
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="h-12 rounded-none border-white/15 bg-black/40 text-white"
                        />
                    </div>
                    {error && (
                        <p data-testid="admin-login-error" className="text-sm text-red-400">
                            {error}
                        </p>
                    )}
                    <Button
                        data-testid="admin-login-button"
                        type="submit"
                        className="h-12 w-full rounded-none bg-[#2563EB] text-sm font-bold uppercase tracking-[0.25em] text-white transition-colors duration-300 hover:bg-white hover:text-black"
                    >
                        Sign in
                    </Button>
                </form>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#050505] text-white" data-testid="admin-dashboard">
            <header className="border-b border-white/10 bg-black/60 backdrop-blur-xl">
                <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
                    <div className="flex items-center gap-3">
                        <img src="/images/logo.png" alt="RAV4 Buyers Club" className="h-10 w-10 bg-white object-contain p-0.5" />
                        <span className="font-display text-sm font-bold uppercase tracking-[0.3em]">
                            Member Dashboard
                        </span>
                    </div>
                    <button
                        data-testid="admin-logout-button"
                        onClick={logout}
                        className="flex items-center gap-2 border border-white/25 px-4 py-2 text-xs font-bold uppercase tracking-[0.2em] transition-colors duration-300 hover:border-[#2563EB] hover:bg-[#2563EB]"
                    >
                        <LogOut className="h-3.5 w-3.5" aria-hidden="true" />
                        Sign out
                    </button>
                </div>
            </header>

            <main className="mx-auto max-w-7xl px-6 py-10">
                <form
                    onSubmit={saveAlert}
                    data-testid="deal-alert-form"
                    className="mb-8 grid grid-cols-1 items-end gap-4 border border-[#2563EB]/40 bg-[#0a0a0a] p-6 md:grid-cols-5"
                >
                    <div className="space-y-2">
                        <Label className="text-zinc-300">Deal alert — trim</Label>
                        <Input
                            data-testid="alert-trim-input"
                            required
                            value={alert.trim}
                            onChange={(e) => setAlert((a) => ({ ...a, trim: e.target.value }))}
                            className="h-11 rounded-none border-white/15 bg-black/40 text-white"
                        />
                    </div>
                    <div className="space-y-2">
                        <Label className="text-zinc-300">Price</Label>
                        <Input
                            data-testid="alert-price-input"
                            required
                            value={alert.price}
                            onChange={(e) => setAlert((a) => ({ ...a, price: e.target.value }))}
                            className="h-11 rounded-none border-white/15 bg-black/40 text-white"
                        />
                    </div>
                    <div className="space-y-2">
                        <Label className="text-zinc-300">Region</Label>
                        <Input
                            data-testid="alert-region-input"
                            required
                            value={alert.region}
                            onChange={(e) => setAlert((a) => ({ ...a, region: e.target.value }))}
                            className="h-11 rounded-none border-white/15 bg-black/40 text-white"
                        />
                    </div>
                    <label className="flex h-11 cursor-pointer items-center gap-3 border border-white/15 px-4">
                        <Checkbox
                            data-testid="alert-enabled-checkbox"
                            checked={alert.enabled}
                            onCheckedChange={(c) => setAlert((a) => ({ ...a, enabled: !!c }))}
                        />
                        <span className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-300">
                            Show on site
                        </span>
                    </label>
                    <Button
                        data-testid="alert-save-button"
                        type="submit"
                        disabled={alertSaving}
                        className="h-11 rounded-none bg-[#2563EB] text-xs font-bold uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:bg-white hover:text-black"
                    >
                        {alertSaving ? "Saving..." : "Save alert"}
                    </Button>
                </form>

                <div className="grid grid-cols-1 gap-px border border-white/10 bg-white/10 sm:grid-cols-3" data-testid="payment-stats">
                    {(() => {
                        const paidCount = members.filter((m) =>
                            ["paid", "allocated", "delivered"].includes(m.status || (m.paid ? "paid" : "reserved"))
                        ).length;
                        const deliveredCount = members.filter((m) => m.status === "delivered").length;
                        const cards = [
                            { label: "Collected", value: `$${(paidCount * 399).toLocaleString()}`, sub: `${paidCount} member${paidCount === 1 ? "" : "s"} paid`, testId: "stat-collected" },
                            { label: "Outstanding", value: `$${((members.length - paidCount) * 399).toLocaleString()}`, sub: `${members.length - paidCount} awaiting payment`, testId: "stat-outstanding" },
                            { label: "Delivered", value: String(deliveredCount), sub: "vehicles in members' hands", testId: "stat-delivered" },
                        ];
                        return cards.map((c) => (
                            <div key={c.label} data-testid={c.testId} className="bg-[#050505] p-6">
                                <p className="text-xs font-bold uppercase tracking-[0.25em] text-zinc-500">{c.label}</p>
                                <p className="mt-2 font-display text-3xl font-extrabold tracking-tight">{c.value}</p>
                                <p className="mt-1 text-xs text-zinc-500">{c.sub}</p>
                            </div>
                        ));
                    })()}
                </div>

                <div className="mt-8 flex flex-wrap items-center justify-between gap-4">
                    <div className="flex items-center gap-2" data-testid="admin-tabs">
                        {Object.entries(TABLES).map(([key, t]) => (
                            <button
                                key={key}
                                data-testid={`admin-tab-${key}`}
                                onClick={() => setTab(key)}
                                className={`px-5 py-2.5 text-xs font-bold uppercase tracking-[0.2em] transition-colors duration-300 ${
                                    tab === key
                                        ? "bg-[#2563EB] text-white"
                                        : "border border-white/25 text-zinc-400 hover:text-white"
                                }`}
                            >
                                {t.label}
                            </button>
                        ))}
                    </div>
                    <p data-testid="admin-member-count" className="text-sm uppercase tracking-[0.2em] text-zinc-400">
                        {filtered.length} {TABLES[tab].label.toLowerCase()}
                    </p>
                    <div className="flex items-center gap-3">
                        {tab === "spot" && (
                            <Button
                                data-testid="admin-blast-button"
                                onClick={() => setBlastOpen((o) => !o)}
                                className="h-11 rounded-none border border-[#2563EB] bg-transparent text-xs font-bold uppercase tracking-[0.2em] text-[#2563EB] transition-colors duration-300 hover:bg-[#2563EB] hover:text-white"
                            >
                                <Megaphone className="mr-2 h-4 w-4" aria-hidden="true" />
                                Blast list
                            </Button>
                        )}
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" aria-hidden="true" />
                            <Input
                                data-testid="admin-search-input"
                                placeholder="Search members..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                className="h-11 w-64 rounded-none border-white/15 bg-black/40 pl-10 text-white"
                            />
                        </div>
                        <Button
                            data-testid="admin-export-csv-button"
                            onClick={exportCsv}
                            className="h-11 rounded-none bg-[#2563EB] text-xs font-bold uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:bg-white hover:text-black"
                        >
                            <Download className="mr-2 h-4 w-4" aria-hidden="true" />
                            Export CSV
                        </Button>
                    </div>
                </div>

                {blastOpen && tab === "spot" && (
                    <form
                        onSubmit={sendBlast}
                        data-testid="blast-form"
                        className="mt-6 grid grid-cols-1 gap-4 border border-[#2563EB]/40 bg-[#0a0a0a] p-6 md:grid-cols-4"
                    >
                        <div className="space-y-2">
                            <Label className="text-zinc-300">Vehicle</Label>
                            <Input
                                data-testid="blast-vehicle-input"
                                required
                                placeholder="2026 RAV4 Woodland AWD"
                                value={blastForm.vehicle}
                                onChange={(e) => setBlastForm((f) => ({ ...f, vehicle: e.target.value }))}
                                className="h-11 rounded-none border-white/15 bg-black/40 text-white"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label className="text-zinc-300">Price</Label>
                            <Input
                                data-testid="blast-price-input"
                                required
                                placeholder="$38,450"
                                value={blastForm.price}
                                onChange={(e) => setBlastForm((f) => ({ ...f, price: e.target.value }))}
                                className="h-11 rounded-none border-white/15 bg-black/40 text-white"
                            />
                        </div>
                        <div className="space-y-2 md:col-span-2">
                            <Label className="text-zinc-300">Message (optional)</Label>
                            <div className="flex gap-3">
                                <Input
                                    data-testid="blast-message-input"
                                    placeholder="On the lot in Austin — first come, first served."
                                    value={blastForm.message}
                                    onChange={(e) => setBlastForm((f) => ({ ...f, message: e.target.value }))}
                                    className="h-11 rounded-none border-white/15 bg-black/40 text-white"
                                />
                                <Button
                                    data-testid="blast-send-button"
                                    type="submit"
                                    disabled={blastSending}
                                    className="h-11 shrink-0 rounded-none bg-[#2563EB] text-xs font-bold uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:bg-white hover:text-black"
                                >
                                    {blastSending ? "Sending..." : "Send blast"}
                                </Button>
                            </div>
                        </div>
                    </form>
                )}

                <div className="mt-8 overflow-x-auto border border-white/10">
                    <table className="w-full text-left text-sm" data-testid="admin-members-table">
                        <thead>
                            <tr className="border-b border-white/10 bg-[#0a0a0a] text-xs uppercase tracking-[0.15em] text-zinc-500">
                                {TABLES[tab].columns.map((c) => (
                                    <th key={c} className="px-4 py-3">
                                        {c}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((m) => (
                                <tr key={m.id} className="border-b border-white/5 transition-colors duration-200 hover:bg-white/5">
                                    {TABLES[tab].row(m).map((cell, i) => (
                                        <td key={i} className={`px-4 py-3 ${i === 0 ? "font-semibold" : "text-zinc-300"}`}>
                                            {cell}
                                        </td>
                                    ))}
                                    {tab === "members" && (
                                        <td className="px-4 py-3">
                                            <Select
                                                value={m.status || "reserved"}
                                                onValueChange={(v) => setStatus(m, v)}
                                            >
                                                <SelectTrigger
                                                    data-testid={`status-select-${m.id}`}
                                                    className={`h-9 w-36 rounded-none text-xs font-bold uppercase tracking-[0.1em] ${STATUS_STYLES[m.status || "reserved"]}`}
                                                >
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {MEMBER_STATUSES.map((s) => (
                                                        <SelectItem
                                                            key={s}
                                                            value={s}
                                                            data-testid={`status-option-${s}`}
                                                            className="text-xs uppercase tracking-[0.1em]"
                                                        >
                                                            {s}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </td>
                                    )}
                                </tr>
                            ))}
                            {filtered.length === 0 && (
                                <tr>
                                    <td colSpan={TABLES[tab].columns.length} className="px-4 py-10 text-center text-zinc-500">
                                        Nothing found.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </main>
        </div>
    );
}
